import { createSlice } from "@reduxjs/toolkit";

import { CLIENT_KEYS } from "../../constants/clientKeys";
import { callApi } from "../../utils/callApi";
import { showError, showSuccess } from "../../utils/notification";
import { queryClient } from "../../utils/reactQueryClient";
import { UploadProgress, uploadWithProgress } from "../../utils/uploadWithProgress";

// Initial state for transcription tasks
const initialState = {
  activeTasks: {}, // Map of taskId to task details
  completedTasks: {}, // Map of taskId to completed task results
};

// Create the transcription slice
const transcriptionSummarySlice = createSlice({
  name: "transcription-summary",
  initialState,
  reducers: {
    // Add a new transcription task
    addTask: (state, action) => {
      const { taskId, caseNumber, metadata } = action.payload;
      state.activeTasks[taskId] = {
        taskId,
        status: "PENDING",
        startTime: Date.now(),
        caseNumber,
        metadata,
      };
    },

    // Update task status
    updateTaskStatus: (state, action) => {
      const { taskId, status, result } = action.payload;
      if (state.activeTasks[taskId]) {
        state.activeTasks[taskId].status = status;
        if (result) {
          state.activeTasks[taskId].result = result;
        }
      }
    },

    // Complete a task and move it to completedTasks
    completeTask: (state, action) => {
      const { taskId, result } = action.payload;
      if (state.activeTasks[taskId]) {
        const task = state.activeTasks[taskId];
        state.completedTasks[taskId] = {
          ...task,
          result,
          endTime: Date.now(),
        };
        delete state.activeTasks[taskId];
      }
    },

    // Delete a completed task
    deleteCompletedTask: (state, action) => {
      const { taskId } = action.payload;
      if (state.completedTasks[taskId]) {
        delete state.completedTasks[taskId];
      }
    },
  },
});

export const { addTask, updateTaskStatus, completeTask, deleteCompletedTask } =
  transcriptionSummarySlice.actions;

/**
 * Kick off transcription + summarization for a multipart body.
 *
 * When `options.onUploadProgress` is provided (the Upload Recording modal), the
 * request goes over XHR so the browser can report byte-level upload progress;
 * otherwise it uses the standard `callApi`/fetch path (session-end, dictation).
 * `options.signal` aborts the in-flight upload.
 */
export const startTranscriptionSummaryTask =
  (formData, options = {}) =>
  async (dispatch) => {
    const { onUploadProgress, signal } = options as {
      onUploadProgress?: (progress: UploadProgress) => void;
      signal?: AbortSignal;
    };
    try {
      // Validate required form data fields before sending
      if (!formData.has("examinee_name") || !formData.get("examinee_name")) {
        showError("Examinee name is required for transcription", dispatch);
        throw new Error("Examinee name is required for transcription");
      }

      const response = onUploadProgress
        ? await uploadWithProgress("transcription/summarize", formData, {
            onProgress: onUploadProgress,
            signal,
          })
        : await callApi("transcription/summarize", "POST", null, formData);

      let caseNumber = null;
      let notes = "";
      const examineeName = formData.get("examinee_name");

      if (formData.has("case_number")) {
        caseNumber = formData.get("case_number");
      }

      if (formData.has("notes")) {
        notes = formData.get("notes") || ""; // Ensure notes is never null
      }

      // Extract task IDs from the response, handling the new response format
      // The response now contains a 'tasks' array with task objects that have task_id and source
      const taskIds = Array.isArray(response)
        ? response.map((task) => task.task_id)
        : response?.tasks
          ? response.tasks.map((task) => task.task_id)
          : [response.task_id];

      // Add each task to the store with notes, examinee_name, and source in metadata
      if (Array.isArray(response)) {
        response.forEach((task) => {
          dispatch(
            addTask({
              taskId: task.task_id,
              caseNumber,
              metadata: { notes, examineeName, source: task.source },
            })
          );
        });
      } else if (response?.tasks) {
        response.tasks.forEach((task) => {
          dispatch(
            addTask({
              taskId: task.task_id,
              caseNumber,
              metadata: { notes, examineeName, source: task.source },
            })
          );
        });
      } else {
        dispatch(
          addTask({
            taskId: response.task_id,
            caseNumber,
            metadata: { notes, examineeName },
          })
        );
      }

      // Overwriting a transcript blanks the previous transcript + summaries on
      // the server at task-start (MDRS-132). Invalidate the cached assessment now
      // so the UI reflects the cleared/TRANSCRIBING state immediately instead of
      // showing stale summaries until the poll completes.
      if (caseNumber) {
        queryClient.invalidateQueries({
          queryKey: [CLIENT_KEYS.ASSESSMENTS, "case", caseNumber],
        });
      }

      // Start polling for each task
      taskIds.forEach((taskId) => {
        dispatch(
          pollTranscriptionStatus({ taskId, caseNumber, notes, examineeName })
        );
      });

      return taskIds;
    } catch (error) {
      const detail = (error as { detail?: string })?.detail;
      showError(detail || "Transcription service is unavailable.", dispatch);
      throw error;
    }
  };

// Resume polling an ALREADY-started VM batch task on page load — no new
// /transcription/summarize call. Needed because the only code that persists the
// VM result runs inside the status-poll loop; if the doctor closed the tab while
// an assessment was still TRANSCRIBING, nothing polls the status endpoint and the
// summary + diarized transcript are never persisted (the row stays stuck at
// TRANSCRIBING). Callers must guard against a task that's already active (see the
// activeTasks check in AudioTranscript) so we don't double-poll in the tab that
// started it.
export const resumeTranscriptionSummaryTask =
  ({
    taskId,
    caseNumber = null,
    notes = "",
    examineeName = "",
  }: {
    taskId: string;
    caseNumber?: string | null;
    notes?: string | null;
    examineeName?: string | null;
  }) =>
  (dispatch) => {
    if (!taskId) return;
    dispatch(
      addTask({
        taskId,
        caseNumber,
        metadata: { notes, examineeName },
      })
    );
    dispatch(pollTranscriptionStatus({ taskId, caseNumber, notes, examineeName }));
  };

export const updateAssessmentWithTranscription =
  (caseNumber, transcriptionData) => async (dispatch) => {
    try {
      if (!caseNumber) {
        showError("Failed to save assessment: No case number provided", dispatch);
        return;
      }

      // First, fetch the assessment details to get the correct ID
      let assessmentDetails;
      try {
        assessmentDetails = await callApi(`assessment/case/${caseNumber}`, "GET");
        if (!assessmentDetails || !assessmentDetails.id) {
          showError(
            `Failed to find assessment details for Case ${caseNumber}`,
            dispatch
          );
          return;
        }
      } catch (error) {
        showError(
          `Failed to get assessment details for Case ${caseNumber}`,
          dispatch
        );
        throw error;
      }

      const assessmentId = assessmentDetails.id;

      // Now create the form data with the correct case_id from assessmentDetails
      const formData = new FormData();
      formData.append("case_id", assessmentDetails.case_id);
      formData.append("case_number", caseNumber);

      // Notes are intentionally NOT sent here (MDRS-132). This save runs on
      // transcription completion carrying a start-time `notes` snapshot (empty for
      // the scribe flow); appending it would clobber the notes the user typed and
      // autosaved during the session. Notes are owned by the autosave path — the
      // PATCH skips the column when it's absent.

      // Add all transcription data fields in a more concise way
      const transcriptionFields = [
        "full_transcript",
        "one_line_summary",
        "full_summary",
        "history_of_injury",
        "history_of_treatment",
        "history_of_progress_over_time",
        "other_relevant_history",
        "educational_occupational_background",
        "mental_state_examination",
        "current_injury_status",
        "past_medical_history",
        "activities_of_daily_living",
        "medication",
        "physical_examination",
      ];

      // Loop through all fields and add them if they exist
      transcriptionFields.forEach((field) => {
        if (transcriptionData[field]) {
          formData.append(field, transcriptionData[field]);
        }
      });

      // Set the status to COMPLETED when saving the assessment
      formData.append("status", "COMPLETED");

      // Save assessment using the assessment ID
      const response = await callApi(
        `assessment/${assessmentId}`,
        "PATCH",
        null,
        formData
      );

      showSuccess(
        `Assessment transcription saved successfully for Case ${caseNumber}`,
        dispatch
      );

      // Refresh assessment details to get the latest data after the update
      try {
        // Invalidate React Query cache for this assessment
        queryClient.invalidateQueries({
          queryKey: [CLIENT_KEYS.ASSESSMENTS, "case", caseNumber],
        });

        // Also invalidate general assessments queries
        queryClient.invalidateQueries({
          queryKey: [CLIENT_KEYS.ASSESSMENTS],
        });
      } catch (_error) {
        showError(
          `Failed to refresh assessment details for Case ${caseNumber}`,
          dispatch
        );
        // Don't throw here since we've already saved the data successfully
      }

      return response;
    } catch (error) {
      showError(
        `Failed to save assessment transcription for Case ${caseNumber}`,
        dispatch
      );
      throw error;
    }
  };

// TODO: update to query with auto refetch
// Total wall-clock cap on polling a single task (MDRS-223). Without it, a VM
// task that hangs and never emits FAILURE is polled forever and the assessment
// sits at TRANSCRIBING with no user-visible resolution. Generous enough for a
// long recording's batch transcription+diarization; a chained new task_id gets
// its own fresh budget (see the SUCCESS/new-task_id branch below).
export const MAX_POLL_DURATION_MS = 20 * 60 * 1000; // 20 minutes

export const pollTranscriptionStatus =
  ({
    taskId,
    caseNumber = null,
    notes = "",
    examineeName = "",
    startedAt = Date.now(),
  }: {
    taskId: string;
    caseNumber: string | null | undefined;
    notes: string | null | undefined;
    examineeName?: string | null | undefined;
    /** Epoch ms when polling THIS task began; threaded through reschedules so
     *  the elapsed cap measures total time, not per-tick. */
    startedAt?: number;
  }) =>
  async (dispatch) => {
    // Stop polling once the cap is exceeded: fully clear the task (so it no
    // longer suppresses auto-export) and tell the user it's still processing
    // rather than looping silently forever. (MDRS-223)
    if (Date.now() - startedAt > MAX_POLL_DURATION_MS) {
      dispatch(completeTask({ taskId, result: { timedOut: true } }));
      dispatch(deleteCompletedTask({ taskId }));
      showError(
        `Transcription is taking longer than expected${
          caseNumber ? ` for Case ${caseNumber}` : ""
        }. It may still be processing — reopen the case later to check, or try again.`,
        dispatch
      );
      return;
    }
    try {
      let statusResponse;
      try {
        statusResponse = await callApi(
          `transcription/summary/status/${taskId}`,
          "GET"
        );
      } catch (error) {
        // Handle any API error by deleting the task and showing an error message
        const errorResponse = error as { detail: string; status?: number };
        dispatch(deleteCompletedTask({ taskId }));

        // Show specific error message based on error type
        const errorMessage = errorResponse?.detail || "Unknown error occurred";
        showError(
          `Transcription failed${caseNumber ? ` for Case ${caseNumber}` : ""}: ${errorMessage}`,
          dispatch
        );
        return;
      }
      const data = statusResponse;

      // Update task status in the store
      dispatch(
        updateTaskStatus({
          taskId,
          status: data.state,
          result: data.status,
        })
      );

      if (data.state === "SUCCESS") {
        // If a new task_id is provided, use it for the next poll
        if (data.status && data.status.task_id) {
          dispatch(
            addTask({
              taskId: data.status.task_id,
              caseNumber,
              metadata: { notes, examineeName },
            })
          );
          dispatch(
            pollTranscriptionStatus({
              taskId: data.status.task_id,
              caseNumber,
              notes,
              examineeName,
            })
          );
        }

        // If we get a non-empty result, it's final
        else if (
          data.status &&
          typeof data.status === "object" &&
          Object.keys(data.status).length > 0
        ) {
          dispatch(completeTask({ taskId, result: data.status }));

          showSuccess(
            `Transcription & Summarization complete${
              caseNumber ? ` for Case ${caseNumber}` : ""
            }.`,
            dispatch
          );

          try {
            // Save the assessment with the transcription data (notes excluded —
            // they're owned by the autosave path; see updateAssessmentWithTranscription).
            await dispatch(
              updateAssessmentWithTranscription(caseNumber, data.status)
            );
            // Cache invalidation happens inside updateAssessmentWithTranscription
          } catch (_error) {
            showError(
              `Failed to save assessment transcription for Case ${caseNumber}`,
              dispatch
            );

            // TODO: Handle error and refetch of assessment details
          }

          // Delete the completed task after processing
          dispatch(deleteCompletedTask({ taskId }));
        }

        // State is SUCCESS but the result is still empty, so poll again
        else {
          setTimeout(
            () =>
              dispatch(
                pollTranscriptionStatus({
                  taskId,
                  caseNumber,
                  notes,
                  examineeName,
                  startedAt,
                })
              ),
            2000
          );
        }
      } else if (data.state === "FAILURE") {
        showError(
          `Transcription & Summarization failed${
            caseNumber ? ` for Case ${caseNumber}` : ""
          }.`,
          dispatch
        );
        // Clear the task on terminal FAILURE (MDRS-223, review #767/#763): a
        // failed task left in activeTasks keeps suppressing auto-export and,
        // once resume-on-load exists, gets re-polled + re-toasted on every
        // reload. Same clean-up the timeout path does.
        dispatch(completeTask({ taskId, result: { failed: true } }));
        dispatch(deleteCompletedTask({ taskId }));
      } else {
        // If not yet in SUCCESS state, wait and poll again
        setTimeout(
          () =>
            dispatch(
              pollTranscriptionStatus({
                taskId,
                caseNumber,
                notes,
                examineeName,
                startedAt,
              })
            ),
          2000
        );
      }
    } catch (err) {
      showError(
        `Unable to check transcription status${
          caseNumber ? ` for Case ${caseNumber}` : ""
        }.`,
        dispatch
      );

      // In case of error, wait and retry polling for the same taskId
      setTimeout(
        () =>
          dispatch(
            pollTranscriptionStatus({
              taskId,
              caseNumber,
              notes,
              examineeName,
              startedAt,
            })
          ),
        5000
      );
    }
  };

export default transcriptionSummarySlice.reducer;
